import type { CalendarFeed } from '../contract';

// The words the calendar settings use. Pure (the clock is passed in), so they can be tested without a screen.

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// "just now", "5 minutes ago", "2 hours ago", "3 days ago".
export function timeAgo(iso: string, now: number): string {
    const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
    if (Number.isNaN(seconds)) return 'a while ago';
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${plural(Math.floor(seconds / 60), 'minute')} ago`;
    if (seconds < 86400) return `${plural(Math.floor(seconds / 3600), 'hour')} ago`;

    return `${plural(Math.floor(seconds / 86400), 'day')} ago`;
}

// One line saying how a calendar is doing.
export function feedStatusLine(feed: CalendarFeed, now: number): string {
    if (feed.status === 'pending') return 'Not read yet.';

    const events = feed.eventCount === undefined ? '' : ` · ${plural(feed.eventCount, 'event')} in the next weeks`;
    if (feed.status === 'ok') return `Read ${feed.lastSuccessAt ? timeAgo(feed.lastSuccessAt, now) : 'recently'}${events}`;

    return `${feed.lastError ?? 'The last reading failed.'}${feed.lastSuccessAt ? ` It last worked ${timeAgo(feed.lastSuccessAt, now)}.` : ''}`;
}

// What a reading did, in a sentence.
export function countsMessage(counts: { created?: number, updated?: number, removed?: number, events?: number }): string {
    const { created = 0, updated = 0, removed = 0, events = 0 } = counts;
    const parts = [
        created ? `${plural(created, 'task')} added` : '',
        updated ? `${plural(updated, 'task')} updated` : '',
        removed ? `${plural(removed, 'task')} removed` : '',
    ].filter(Boolean);

    if (parts.length) return `${parts.join(', ')}.`;
    return events ? 'Everything is up to date.' : 'No events found in the next weeks.';
}

export type CalendarProvider = 'google' | 'apple';

export const providerLabel = (provider: CalendarProvider) => (provider === 'apple' ? 'Apple Calendar' : 'Google Calendar');

// Whose calendar address this looks like, or null. It catches a wrong paste before asking the server, which checks again and is the one that decides.
// Google's is the secret address in iCal format; Apple's is a public calendar's link, which Apple writes as webcal://.
export function calendarAddressProvider(text: string): CalendarProvider | null {
    const address = text.trim();
    if (/^https:\/\/calendar\.google\.com\/calendar\/ical\/[^\s/]+\/(?:private-[A-Za-z0-9]+|public)\/basic\.ics$/.test(address)) return 'google';
    if (/^(?:https|webcal):\/\/p\d{1,3}-caldav\.icloud\.com\/published\/\d{1,3}\/[A-Za-z0-9_-]{16,400}$/i.test(address)) return 'apple';
    return null;
}

export const looksLikeCalendarAddress = (text: string) => calendarAddressProvider(text) !== null;
